(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initReveal(scope = document) {
    const items = scope.querySelectorAll('.mv3-reveal:not([data-mv3-reveal-ready]), .mv-reveal:not([data-mv3-reveal-ready])');
    items.forEach((item, index) => {
      item.dataset.mv3RevealReady = 'true';
      item.style.setProperty('--mv3-delay', `${Math.min(index % 6, 5) * 70}ms`);
    });
    if (reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach(item => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    items.forEach(item => observer.observe(item));
  }

  function initHero(scope = document) {
    scope.querySelectorAll('[data-mv3-hero-stage]:not([data-mv3-ready])').forEach(stage => {
      stage.dataset.mv3Ready = 'true';
      const cards = [...stage.querySelectorAll('[data-mv3-hero-card]')];
      const dots = [...stage.querySelectorAll('[data-mv3-hero-dot]')];
      if (!cards.length) return;
      let current = 0;
      let timer;
      const activate = index => {
        current = (index + cards.length) % cards.length;
        cards.forEach((card, cardIndex) => {
          const relative = (cardIndex - current + cards.length) % cards.length;
          card.classList.toggle('is-active', relative === 0);
          card.dataset.stackPosition = String(relative);
          card.setAttribute('aria-hidden', relative === 0 ? 'false' : 'true');
        });
        dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === current));
      };
      const stop = () => window.clearInterval(timer);
      const start = () => {
        stop();
        if (!reducedMotion && cards.length > 1) timer = window.setInterval(() => activate(current + 1), 4400);
      };
      dots.forEach(dot => dot.addEventListener('click', () => {
        activate(Number(dot.dataset.index || 0));
        start();
      }));
      stage.addEventListener('mouseenter', stop);
      stage.addEventListener('mouseleave', start);
      stage.addEventListener('focusin', stop);
      stage.addEventListener('focusout', start);
      if (!reducedMotion && window.matchMedia('(pointer:fine)').matches) {
        stage.addEventListener('pointermove', event => {
          const rect = stage.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width - 0.5;
          const y = (event.clientY - rect.top) / rect.height - 0.5;
          stage.style.setProperty('--mv3-stage-x', `${x * 18}px`);
          stage.style.setProperty('--mv3-stage-y', `${y * 18}px`);
        });
        stage.addEventListener('pointerleave', () => {
          stage.style.setProperty('--mv3-stage-x', '0px');
          stage.style.setProperty('--mv3-stage-y', '0px');
        });
      }
      activate(0);
      start();
    });
  }

  function initProblemFinder(scope = document) {
    scope.querySelectorAll('[data-mv3-problem]:not([data-mv3-ready])').forEach(root => {
      root.dataset.mv3Ready = 'true';
      const tabs = [...root.querySelectorAll('[data-mv3-problem-tab]')];
      const panels = [...root.querySelectorAll('[data-mv3-problem-panel]')];
      const activate = targetId => {
        tabs.forEach(tab => {
          const active = tab.dataset.target === targetId;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
        });
        panels.forEach(panel => panel.classList.toggle('is-active', panel.id === targetId));
      };
      tabs.forEach(tab => tab.addEventListener('click', () => activate(tab.dataset.target)));
    });
  }

  function initDragTracks(scope = document) {
    scope.querySelectorAll('[data-mv3-drag-track]:not([data-mv3-ready])').forEach(track => {
      track.dataset.mv3Ready = 'true';

      let down = false;
      let dragging = false;
      let suppressNextClick = false;
      let startX = 0;
      let startY = 0;
      let startScroll = 0;
      let pointerId = null;

      track.addEventListener('pointerdown', event => {
        // Touch devices already have reliable native horizontal scrolling.
        // Leaving touch gestures native keeps taps on product links fully clickable.
        if (event.pointerType !== 'mouse' || event.button !== 0) return;

        down = true;
        dragging = false;
        suppressNextClick = false;
        startX = event.clientX;
        startY = event.clientY;
        startScroll = track.scrollLeft;
        pointerId = event.pointerId;
      });

      track.addEventListener('pointermove', event => {
        if (!down || event.pointerType !== 'mouse') return;

        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;

        // A normal click can move a few pixels. Only enter drag mode after
        // a clear horizontal gesture, so links remain clickable.
        if (!dragging) {
          if (Math.abs(deltaX) < 10 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
          dragging = true;
          track.classList.add('is-dragging');
          track.setPointerCapture?.(event.pointerId);
        }

        event.preventDefault();
        track.scrollLeft = startScroll - deltaX * 1.15;
      });

      const release = event => {
        if (!down) return;

        if (dragging) {
          suppressNextClick = true;
          window.setTimeout(() => { suppressNextClick = false; }, 350);
        }
        down = false;
        dragging = false;
        track.classList.remove('is-dragging');

        const id = event?.pointerId ?? pointerId;
        if (id != null && track.hasPointerCapture?.(id)) {
          track.releasePointerCapture(id);
        }
        pointerId = null;
      };

      track.addEventListener('pointerup', release);
      track.addEventListener('pointercancel', release);
      track.addEventListener('pointerleave', event => {
        if (event.pointerType === 'mouse' && down && dragging) release(event);
      });

      // Prevent navigation only after a real drag. Ordinary clicks pass through.
      track.addEventListener('click', event => {
        if (!suppressNextClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressNextClick = false;
      }, true);
    });
  }

  function initTilt(scope = document) {
    if (reducedMotion || !window.matchMedia('(pointer:fine)').matches) return;
    scope.querySelectorAll('[data-mv3-tilt]:not([data-mv3-tilt-ready])').forEach(card => {
      card.dataset.mv3TiltReady = 'true';
      card.addEventListener('pointermove', event => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        card.style.setProperty('--mv3-rotate-y', `${x * 4}deg`);
        card.style.setProperty('--mv3-rotate-x', `${y * -4}deg`);
        card.style.setProperty('--mv3-glow-x', `${(x + 0.5) * 100}%`);
        card.style.setProperty('--mv3-glow-y', `${(y + 0.5) * 100}%`);
      });
      card.addEventListener('pointerleave', () => {
        card.style.setProperty('--mv3-rotate-y', '0deg');
        card.style.setProperty('--mv3-rotate-x', '0deg');
      });
    });
  }

  function initAutoVideos(scope = document) {
    const videos = scope.querySelectorAll('.mv3-reel-card video, [data-mv3-auto-video] video');
    if (!videos.length || reducedMotion || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const video = entry.target;
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      });
    }, { threshold: 0.35 });
    videos.forEach(video => {
      if (video.dataset.mv3Observed) return;
      video.dataset.mv3Observed = 'true';
      observer.observe(video);
    });
  }

  function initProductGallery(scope = document) {
    scope.querySelectorAll('[data-mv3-gallery]:not([data-mv3-ready])').forEach(gallery => {
      gallery.dataset.mv3Ready = 'true';
      const slides = [...gallery.querySelectorAll('[data-mv3-media]')];
      const thumbs = [...gallery.querySelectorAll('[data-mv3-thumb]')];
      if (!slides.length) return;
      const activate = index => {
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
          const video = slide.querySelector('video');
          if (video) {
            if (active && !reducedMotion) video.play().catch(() => {});
            else video.pause();
          }
        });
        thumbs.forEach((thumb, thumbIndex) => {
          thumb.classList.toggle('is-active', thumbIndex === index);
          thumb.setAttribute('aria-selected', String(thumbIndex === index));
        });
      };
      thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => activate(index)));
      activate(0);
    });
  }

  function initSwitchers(scope = document) {
    scope.querySelectorAll('[data-mv3-switcher]:not([data-mv3-ready])').forEach(root => {
      root.dataset.mv3Ready = 'true';
      const buttons = [...root.querySelectorAll('[data-mv3-switch-button]')];
      const panels = [...root.querySelectorAll('[data-mv3-switch-panel]')];
      const activate = target => {
        buttons.forEach(button => {
          const active = button.dataset.target === target;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', String(active));
        });
        panels.forEach(panel => panel.classList.toggle('is-active', panel.id === target));
      };
      buttons.forEach(button => button.addEventListener('click', () => activate(button.dataset.target)));
    });
  }

  function initStickyBuy(scope = document) {
    scope.querySelectorAll('[data-mv3-product-root]:not([data-mv3-product-ready])').forEach(root => {
      root.dataset.mv3ProductReady = 'true';
      const sticky = root.querySelector('[data-mv3-sticky-buy]');
      const buybox = root.querySelector('[data-mv3-buybox]');
      const mainButton = root.querySelector('[data-mv3-atc]');
      const stickyButton = root.querySelector('[data-mv3-sticky-button]');
      if (sticky && buybox && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => sticky.classList.toggle('is-visible', !entry.isIntersecting));
        }, { threshold: 0.05 });
        observer.observe(buybox);
      }
      stickyButton?.addEventListener('click', () => {
        if (mainButton && !mainButton.disabled) mainButton.click();
      });
    });
  }

  function initProgress(scope = document) {
    scope.querySelectorAll('[data-mv3-progress]:not([data-mv3-ready])').forEach(bar => {
      bar.dataset.mv3Ready = 'true';
      const update = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const value = max > 0 ? Math.min(1, window.scrollY / max) : 0;
        bar.style.setProperty('--mv3-progress', `${value * 100}%`);
      };
      update();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
    });
  }


  function initMobileMenu(scope = document) {
    scope.querySelectorAll('.mv-mobile-menu details:not([data-mv-menu-ready])').forEach(details => {
      details.dataset.mvMenuReady = 'true';
      const links = [...details.querySelectorAll('.mv-mobile-panel a')];
      const sync = () => {
        document.documentElement.classList.toggle('mv-menu-open', details.open);
        details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open));
      };
      details.addEventListener('toggle', sync);
      links.forEach(link => link.addEventListener('click', () => {
        details.open = false;
        sync();
      }));
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && details.open) {
          details.open = false;
          sync();
          details.querySelector('summary')?.focus();
        }
      });
      sync();
    });
  }

  function initAll(scope = document) {
    initReveal(scope);
    initHero(scope);
    initProblemFinder(scope);
    initDragTracks(scope);
    initTilt(scope);
    initAutoVideos(scope);
    initProductGallery(scope);
    initSwitchers(scope);
    initStickyBuy(scope);
    initProgress(scope);
    initMobileMenu(scope);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();
